/**
 * Unsaved-changes guard for the settings pages with an explicit Save button
 * (HS2-HV4R7C): Organization › Profile, › Verdict Thresholds and › Landed-cost
 * Assumptions. Each page's form starts from a baseline derived from the loaded
 * settings exactly as its view renders it; the page is dirty while any live field
 * differs. Pure logic plus a minimal DOM surface, so it is unit-testable without
 * a browser.
 */
import { DEFAULT_VERDICT_THRESHOLDS, type VerdictThresholds } from '../../shared/verdict-thresholds.js';
import type { SettingsTab } from './context.js';
import type { SettingsPage } from './settings-nav.js';

export type ExplicitSavePage = 'profile' | 'verdict-thresholds' | 'landed-cost';

type FieldKind = 'text' | 'number' | 'select';

interface FormField {
  name: string;
  kind: FieldKind;
}

const THRESHOLD_KEYS: readonly (keyof VerdictThresholds)[] = [
  'fairPct',
  'slightlyHighPct',
  'highPct',
  'belowMarketPct',
  'importPreferenceMarginPct',
];
const ASSUMPTION_KEYS = ['insurancePct', 'customsDutyPct', 'salesTaxPct', 'financingCostPct'] as const;

const FIELDS: Record<ExplicitSavePage, readonly FormField[]> = {
  profile: [
    { name: 'p_name', kind: 'text' },
    { name: 'p_country', kind: 'text' },
    { name: 'p_currency', kind: 'text' },
    { name: 'p_grouping', kind: 'select' },
  ],
  'verdict-thresholds': THRESHOLD_KEYS.map((key) => ({ name: `t_${key}`, kind: 'number' })),
  'landed-cost': ASSUMPTION_KEYS.map((key) => ({ name: `a_${key}`, kind: 'number' })),
};

/** The explicit-save page at this settings location, or null when the page saves on change. */
export function explicitSavePage(view: string, area: SettingsTab, page: SettingsPage): ExplicitSavePage | null {
  if (view !== 'settings' || area !== 'organization') return null;
  return page === 'profile' || page === 'verdict-thresholds' || page === 'landed-cost' ? page : null;
}

const text = (v: unknown): string => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '');
const record = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

/** Each field's saved value as form text, matching what the page's view renders. */
export function settingsFormBaseline(
  page: ExplicitSavePage,
  settings: Record<string, unknown>,
): Record<string, string> {
  const profile = record(settings.profile);
  switch (page) {
    case 'profile':
      return {
        p_name: text(profile.name),
        p_country: text(profile.country),
        p_currency: text(profile.baseCurrency),
        p_grouping: typeof profile.numberGrouping === 'string' ? profile.numberGrouping : 'international',
      };
    case 'verdict-thresholds': {
      const thresholds = record(profile.verdictThresholds);
      return Object.fromEntries(
        THRESHOLD_KEYS.map((key) => [`t_${key}`, text(thresholds[key] ?? DEFAULT_VERDICT_THRESHOLDS[key])]),
      );
    }
    case 'landed-cost': {
      const assumptions = record(settings.assumptions);
      return Object.fromEntries(ASSUMPTION_KEYS.map((key) => [`a_${key}`, text(assumptions[key])]));
    }
  }
}

/** Equal as saved: numbers compare by value ("5" = "5.0"), everything else as trimmed text. */
function sameValue(kind: FieldKind, saved: string, live: string): boolean {
  if (kind === 'number' && saved !== '' && live !== '') {
    const a = Number(saved);
    const b = Number(live);
    if (Number.isFinite(a) && Number.isFinite(b)) return a === b;
  }
  return saved === live;
}

/** The names of fields whose live value differs from the saved baseline. */
export function changedSettingsFields(
  page: ExplicitSavePage,
  baseline: Record<string, string>,
  live: Record<string, string | null>,
): string[] {
  return FIELDS[page]
    .filter(({ name, kind }) => {
      const value = live[name];
      // A field not rendered yet (still mounting) is not an edit.
      return value !== null && value !== undefined && !sameValue(kind, baseline[name] ?? '', value);
    })
    .map(({ name }) => name);
}

/** The slice of a form control the guard reads and resets. */
export interface FormControl {
  value?: string;
}

export interface FormRoot {
  querySelector(selector: string): FormControl | null;
}

/** A control's live value from its native input or select host. */
function liveValue(control: FormControl): string {
  return (control.value ?? '').trim();
}

/** The page's live field values; a field missing from the DOM reads as null. */
export function readSettingsForm(root: FormRoot, page: ExplicitSavePage): Record<string, string | null> {
  return Object.fromEntries(
    FIELDS[page].map(({ name }) => {
      const control = root.querySelector(`[name="${name}"]`);
      return [name, control === null ? null : liveValue(control)];
    }),
  );
}

/** Put every field back to its saved value (the "Discard changes" choice). */
export function resetSettingsForm(root: FormRoot, page: ExplicitSavePage, baseline: Record<string, string>): void {
  for (const { name } of FIELDS[page]) {
    const control = root.querySelector(`[name="${name}"]`);
    if (control === null) continue;
    const value = baseline[name] ?? '';
    control.value = value;
  }
}

/** Where a guarded click would take the viewer, for deciding whether it leaves the page. */
export interface NavigationContext {
  area: SettingsTab;
  page: SettingsPage;
}

/**
 * Whether activating an element with this `data-action` leaves the current settings
 * page. Actions that only fold the sidebar, toggle a rail, or reselect the page
 * being edited are not navigation.
 */
export function leavesSettingsPage(
  action: string,
  detail: { itemId?: string | null; segment?: string | null },
  at: NavigationContext,
): boolean {
  switch (action) {
    case 'settings-page':
      return detail.itemId !== `${at.area}:${at.page}`;
    case 'settings-area':
      // The open area's header folds it; another area opens its last page.
      return detail.itemId !== at.area;
    case 'seg-view':
      return detail.segment !== 'settings';
    case 'go-settings-org':
      return at.area !== 'organization';
    case 'go-settings':
    case 'go-settings-project':
    case 'go-settings-personal':
    case 'go-library-org':
    case 'go-library-project':
    case 'back-to-list':
    case 'new-demand':
    case 'switch-org':
    case 'signout':
    case 'stop-acting':
      return true;
    default:
      return false;
  }
}
